import { Fragment, useRef, useState } from 'react'

import { IActionRepeaterValue } from '@makyoapp/makyo-common'
import { ColorVariant, CornerRadius, HoverEffectVariant, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { CompIconButtonProps } from './comp'

export interface CompIconButtonWrapperCore {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompIconButtonProps, ref: React.RefObject<HTMLButtonElement>) => React.ReactNode
}

/**
 * CompIconButtonWrapperCore
 */
export function CompIconButtonWrapperCore({
  infos: { component, settings, currentParentBg, containerClient, detailTableId, detailRowIndex },
  action: { simulateAction },
  children,
}: CompIconButtonWrapperCore) {
  const btnRef = useRef<any>(null)
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const icon = useParseVariableSelect<string>(
    settings[ComponentSettingKey.IconButtonDataIcon],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // Convert icon if it's a string array
  const processedIcon = typeof icon === 'string' && icon.startsWith('[') ? JSON.parse(icon)[0] : icon

  const iconActive = useParseVariableSelect<string>(
    settings[ComponentSettingKey.IconButtonDataIconActive],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const badge = useParseVariableText(
    settings[ComponentSettingKey.IconButtonDataBadge],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const isToggleButton = !!component.settings?.[ComponentSettingKey.IconButtonDataToggle]
  const toggleValue = useParseVariableSelect<string>(
    settings[ComponentSettingKey.IconButtonDataToggle],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const isToggledValue = isToggleButton && toggleValue?.toLowerCase() === 'true'

  // toggle state
  const [isToggled, setIsToggled] = useState(isToggledValue)

  const handleToggle = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    setIsToggled(!isToggled)
  }

  // ========================= design =========================
  // base
  const alignment = settings[ComponentSettingKey.IconButtonDesignHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.IconButtonDesignMargin] as RawSpacingInput
  const padding = settings[ComponentSettingKey.IconButtonDesignPadding] as RawSpacingInput

  // icon-button
  const cornerRadius = settings[ComponentSettingKey.IconButtonDesignShape] as CornerRadius
  const iconSize = getSafely(settings, [ComponentSettingKey.IconButtonDesignIconSize], [20])[0]
  const colorVariant = getSafely(
    settings,
    [ComponentSettingKey.IconButtonDesignIconColorVariant],
    'primary-40'
  ) as ColorVariant
  const inactiveIconColor = getSafely(
    settings,
    [ComponentSettingKey.IconButtonDesignIconInactiveColor],
    'neutral-80'
  ) as ColorVariant

  // color
  const activeIconColorType = settings[ComponentSettingKey.IconButtonDesignActiveColor] || 'adaptive'
  const inactiveIconColorType = settings[ComponentSettingKey.IconButtonDesignInActiveColor] || 'adaptive'
  const activeIconColorSolid =
    (settings[ComponentSettingKey.IconButtonDesignActiveColorSolid] as ColorVariant) || 'primary-40'
  const inactiveIconColorSolid =
    (settings[ComponentSettingKey.IconButtonDesignInActiveColorSolid] as ColorVariant) || 'primary-40'

  const hoverEffect = settings[ComponentSettingKey.IconButtonDesignHoverEffect] as HoverEffectVariant

  // ========================= actions =========================
  const clickActions = getSafely(settings, [ComponentSettingKey.IconButtonActionClick], []) as IActionRepeaterValue[]

  // todo
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    if (clickActions && clickActions.length > 0) {
      simulateAction({
        actions: clickActions,
        elRef: btnRef,
      })
    }
  }

  // check if theres no icon
  if (!icon || !icon.trim().length) return null
  const iconButtonProps: CompIconButtonProps = {
    // base
    id: component._id,
    alignment,
    margin,
    padding,
    backgroundColor: currentParentBg,

    activeColorType: activeIconColorType,
    inactiveColorType: inactiveIconColorType,
    activeColorSolid: activeIconColorSolid,
    inactiveColorSolid: inactiveIconColorSolid,

    // icon-button
    isToggled,
    onToggle: handleToggle,
    onClick: handleClick,
    icon: processedIcon,
    iconActive: iconActive,
    iconSize,
    colorVariant,
    cornerRadius,
    badgeText: badge,
    bgColor: currentParentBg,
    inactiveColor: inactiveIconColor,
    devMode: isDevelopmentMode,

    hoverEffect,
    hoverIntensity: 'md',
  }

  // if (isDevelopmentMode) {
  //   return (
  //     <div className='relative border border-dashed' style={{ borderColor: '#333' }}>
  //       {children(iconButtonProps, btnRef)}
  //       <CompOverlayDevmode handleClick={handleClick} />
  //     </div>
  //   )
  // }

  return children(iconButtonProps, btnRef)
}
